import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { setTokens, apiClient } from '../utils/apiClient';
import AxiomusLogo from './AxiomusLogo';

const BASE = process.env.REACT_APP_API_BASE_URL;

const INPUT = "w-full px-4 py-2.5 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2";
const INPUT_CYAN   = INPUT + " focus:ring-cyan-500";
const INPUT_ORANGE = INPUT + " focus:ring-orange-400";

function Back({ onClick }) {
    return (
        <button type="button" onClick={onClick}
            className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            ← Retour
        </button>
    );
}

function ErrorMsg({ msg }) {
    if (!msg) return null;
    return <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{msg}</p>;
}

function Login({ onLogin }) {
    // modes: 'choice' | 'login' | 'register_type' | 'register_student' | 'register_parent'
    const [mode, setMode]         = useState('choice');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName]   = useState('');
    const [email, setEmail]         = useState('');
    const [password, setPassword]   = useState('');
    const [confirm, setConfirm]     = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [niveau, setNiveau]       = useState('');
    const [parentEmail, setParentEmail] = useState('');
    const [linkCode, setLinkCode]   = useState('');
    const [inviteLink, setInviteLink]   = useState('');
    const [copied, setCopied]     = useState(false);
    const [error, setError]       = useState('');
    const [loading, setLoading]   = useState(false);

    // Lire ?register=parent&code=XXXX dans l'URL au chargement
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('register') === 'parent') {
            const code = params.get('code') || '';
            setLinkCode(code.toUpperCase());
            setMode('register_parent');
            // Nettoyer l'URL sans recharger
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    const reset = () => {
        setFirstName(''); setLastName(''); setEmail(''); setPassword(''); setConfirm('');
        setBirthDate(''); setNiveau(''); setParentEmail(''); setLinkCode(''); setInviteLink('');
        setCopied(false); setError('');
    };

    const handleGoogleSuccess = (credentialResponse) => {
        const decoded = jwtDecode(credentialResponse.credential);
        apiClient(`${BASE}/login.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ google_id: decoded.sub, name: decoded.name, email: decoded.email }),
        })
        .then(r => r.json())
        .then(data => {
            if (data.success) {
                setTokens(data.user.token, data.user.refresh_token);
                const isDark = data.user.dark === 1 || data.user.dark === '1' || data.user.dark === true;
                onLogin(data.user.id, data.user.name, data.user.role, isDark, data.user.first_name, data.user.onboarding_completed);
            } else {
                setError(data.message || 'Erreur de connexion.');
            }
        })
        .catch(() => setError('Erreur de connexion au serveur.'));
    };

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res  = await apiClient(`${BASE}/login.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            if (data.success) {
                setTokens(data.user.token, data.user.refresh_token);
                const isDark = data.user.dark === 1 || data.user.dark === '1' || data.user.dark === true;
                onLogin(data.user.id, data.user.name, data.user.role, isDark, data.user.first_name, data.user.onboarding_completed);
            } else {
                setError(data.message || 'Email ou mot de passe incorrect.');
            }
        } catch {
            setError('Erreur de connexion au serveur.');
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterStudent = async (e) => {
        e.preventDefault();
        setError('');
        if (!firstName.trim()) { setError('Le prénom est requis.'); return; }
        if (password !== confirm) { setError('Les mots de passe ne correspondent pas.'); return; }
        if (password.length < 8)  { setError('Le mot de passe doit contenir au moins 8 caractères.'); return; }
        setLoading(true);
        try {
            const res  = await apiClient(`${BASE}/register.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ first_name: firstName, last_name: lastName, email, password, birth_date: birthDate, niveau, parent_email: parentEmail }),
            });
            const data = await res.json();
            if (data.success) {
                if (data.invitation_link) {
                    // Montrer le lien avant de rediriger
                    setInviteLink(data.invitation_link);
                    setLoading(false);
                    // Connecter après que l'utilisateur a vu le lien
                    setTimeout(() => {
                        setTokens(data.user.token, data.user.refresh_token);
                        onLogin(data.user.id, data.user.name, data.user.role, false, data.user.first_name, data.user.onboarding_completed);
                    }, 8000);
                } else {
                    setTokens(data.user.token, data.user.refresh_token);
                    onLogin(data.user.id, data.user.name, data.user.role, false, data.user.first_name, data.user.onboarding_completed);
                }
            } else {
                setError(data.message || 'Erreur lors de la création du compte.');
            }
        } catch {
            setError('Erreur de connexion au serveur.');
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterParent = async (e) => {
        e.preventDefault();
        setError('');
        if (!firstName.trim()) { setError('Le prénom est requis.'); return; }
        if (password !== confirm) { setError('Les mots de passe ne correspondent pas.'); return; }
        if (password.length < 8)  { setError('Le mot de passe doit contenir au moins 8 caractères.'); return; }
        setLoading(true);
        try {
            const res  = await apiClient(`${BASE}/register_parent.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ first_name: firstName, last_name: lastName, email, password, link_code: linkCode }),
            });
            const data = await res.json();
            if (data.success) {
                setTokens(data.user.token, data.user.refresh_token);
                onLogin(data.user.id, data.user.name, data.user.role, false, data.user.first_name, data.user.onboarding_completed);
            } else {
                setError(data.message || 'Erreur lors de la création du compte.');
            }
        } catch {
            setError('Erreur de connexion au serveur.');
        } finally {
            setLoading(false);
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(inviteLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 relative overflow-hidden p-6 font-sans transition-colors duration-300">
            <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-purple-500/10 blur-[120px] pointer-events-none dark:bg-purple-600/20" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-cyan-500/10 blur-[120px] pointer-events-none dark:bg-cyan-600/20" />

            <div className="flex flex-wrap w-full max-w-4xl min-h-[450px] bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-3xl overflow-hidden border border-gray-300 dark:border-white/[0.08] shadow-2xl relative z-10 transition-colors duration-300">

                {/* Left panel */}
                <div className="flex-1 min-w-[300px] p-12 flex flex-col justify-center relative overflow-hidden">
                    <div className="mb-6"><AxiomusLogo height={72} /></div>
                    <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed transition-colors duration-300">
                        La plateforme idéale de mise en relation entre étudiants et professeurs de mathématiques.
                        <br /><br />
                        Connecte-toi pour accéder à ton espace personnel, planifier tes cours et poser tes questions.
                    </p>
                </div>

                {/* Right panel */}
                <div className="flex-1 min-w-[300px] p-12 flex flex-col items-center justify-center bg-gray-100/80 dark:bg-black/20 border-l border-gray-300 dark:border-white/[0.05] relative z-10 transition-colors duration-300">

                    {/* ── Choix initial ────────────────────────────────────── */}
                    {mode === 'choice' && (
                        <>
                            <h2 className="text-2xl font-semibold mb-8 text-gray-800 dark:text-white">Prêt à commencer ?</h2>
                            <div className="rounded-xl overflow-hidden hover:scale-105 hover:shadow-[0_0_30px_rgba(168,85,247,0.4)] transition-all duration-300 mb-6">
                                <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError('Connexion Google échouée.')} useOneTap />
                            </div>
                            <div className="flex items-center w-full gap-3 mb-6">
                                <div className="flex-1 h-px bg-gray-300 dark:bg-white/10" />
                                <span className="text-xs text-gray-400">ou</span>
                                <div className="flex-1 h-px bg-gray-300 dark:bg-white/10" />
                            </div>
                            <button onClick={() => { reset(); setMode('login'); }}
                                className="w-full py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-medium transition-colors mb-3">
                                Connexion avec email
                            </button>
                            <button onClick={() => { reset(); setMode('register_type'); }}
                                className="w-full py-2.5 rounded-xl border border-gray-300 dark:border-white/20 text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-white/5 font-medium transition-colors">
                                Créer un compte
                            </button>
                        </>
                    )}

                    {/* ── Choix type de compte ─────────────────────────────── */}
                    {mode === 'register_type' && (
                        <div className="w-full flex flex-col gap-4">
                            <div className="mb-2">
                                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-1">Quel est votre profil ?</h2>
                                <p className="text-xs text-gray-400">Choisissez le type de compte à créer.</p>
                            </div>

                            <button onClick={() => { reset(); setMode('register_student'); }}
                                className="w-full flex items-start gap-4 p-4 rounded-2xl border-2 border-gray-200 dark:border-white/10 hover:border-cyan-400 dark:hover:border-cyan-500 bg-white/60 dark:bg-white/5 hover:bg-cyan-50 dark:hover:bg-cyan-900/10 transition-all text-left group">
                                <span className="text-2xl mt-0.5">🎓</span>
                                <div>
                                    <p className="font-semibold text-gray-800 dark:text-white group-hover:text-cyan-700 dark:group-hover:text-cyan-300 transition-colors">Je suis un élève</p>
                                    <p className="text-xs text-gray-400 mt-0.5">Accès aux cours, exercices, planning et coaching IA</p>
                                </div>
                            </button>

                            <button onClick={() => { reset(); setMode('register_parent'); }}
                                className="w-full flex items-start gap-4 p-4 rounded-2xl border-2 border-gray-200 dark:border-white/10 hover:border-orange-400 dark:hover:border-orange-500 bg-white/60 dark:bg-white/5 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-all text-left group">
                                <span className="text-2xl mt-0.5">👨‍👩‍👧</span>
                                <div>
                                    <p className="font-semibold text-gray-800 dark:text-white group-hover:text-orange-700 dark:group-hover:text-orange-300 transition-colors">Je suis un parent</p>
                                    <p className="text-xs text-gray-400 mt-0.5">Suivi de la scolarité et gestion de l'abonnement</p>
                                </div>
                            </button>

                            <Back onClick={() => { reset(); setMode('choice'); }} />
                        </div>
                    )}

                    {/* ── Connexion ────────────────────────────────────────── */}
                    {mode === 'login' && (
                        <form onSubmit={handleEmailLogin} className="w-full flex flex-col gap-4">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Connexion</h2>
                            <ErrorMsg msg={error} />
                            <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required className={INPUT_CYAN} />
                            <input type="password" placeholder="Mot de passe" value={password} onChange={e => setPassword(e.target.value)} required className={INPUT_CYAN} />
                            <button type="submit" disabled={loading}
                                className="w-full py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-medium transition-colors">
                                {loading ? 'Connexion…' : 'Se connecter'}
                            </button>
                            <Back onClick={() => { reset(); setMode('choice'); }} />
                        </form>
                    )}

                    {/* ── Inscription élève ────────────────────────────────── */}
                    {mode === 'register_student' && (
                        <form onSubmit={handleRegisterStudent} className="w-full flex flex-col gap-3">
                            <div className="mb-1">
                                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Créer un compte élève</h2>
                            </div>
                            <ErrorMsg msg={error} />

                            {inviteLink ? (
                                <div className="flex flex-col gap-3">
                                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30 rounded-xl px-4 py-3">
                                        <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">Compte créé !</p>
                                        <p className="text-xs text-green-700 dark:text-green-300">Un email a été envoyé à votre parent. Vous pouvez aussi lui partager ce lien directement :</p>
                                    </div>
                                    <div className="flex gap-2 items-center bg-gray-100 dark:bg-white/5 rounded-xl px-3 py-2">
                                        <span className="flex-1 text-xs text-gray-600 dark:text-gray-300 truncate font-mono">{inviteLink}</span>
                                        <button type="button" onClick={copyLink}
                                            className="shrink-0 text-xs px-2.5 py-1 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white font-medium transition-colors">
                                            {copied ? 'Copié !' : 'Copier'}
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-gray-400 text-center">Connexion dans quelques secondes…</p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex gap-2">
                                        <input type="text" placeholder="Prénom *" value={firstName} onChange={e => setFirstName(e.target.value)} required className={INPUT_CYAN + " flex-1"} />
                                        <input type="text" placeholder="Nom" value={lastName} onChange={e => setLastName(e.target.value)} className={INPUT_CYAN + " flex-1"} />
                                    </div>
                                    <input type="email" placeholder="Email *" value={email} onChange={e => setEmail(e.target.value)} required className={INPUT_CYAN} />
                                    <input type="password" placeholder="Mot de passe (8 caractères min.) *" value={password} onChange={e => setPassword(e.target.value)} required className={INPUT_CYAN} />
                                    <input type="password" placeholder="Confirmer le mot de passe *" value={confirm} onChange={e => setConfirm(e.target.value)} required className={INPUT_CYAN} />
                                    <div className="flex gap-2">
                                        <div className="flex-1">
                                            <input type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)}
                                                className={INPUT_CYAN + " text-sm"} />
                                            <p className="text-[10px] text-gray-400 mt-0.5">Date de naissance</p>
                                        </div>
                                        <div className="flex-1">
                                            <select value={niveau} onChange={e => setNiveau(e.target.value)} className={INPUT_CYAN + " text-sm"}>
                                                <option value="">Classe / niveau</option>
                                                <optgroup label="Collège">
                                                    <option value="6ème">6ème</option>
                                                    <option value="5ème">5ème</option>
                                                    <option value="4ème">4ème</option>
                                                    <option value="3ème">3ème</option>
                                                </optgroup>
                                                <optgroup label="Lycée">
                                                    <option value="Seconde">Seconde</option>
                                                    <option value="Première">Première</option>
                                                    <option value="Terminale">Terminale</option>
                                                </optgroup>
                                                <optgroup label="Supérieur">
                                                    <option value="CPGE 1">CPGE 1ère année</option>
                                                    <option value="CPGE 2">CPGE 2ème année</option>
                                                    <option value="BTS">BTS</option>
                                                    <option value="Licence 1">Licence 1</option>
                                                    <option value="Licence 2">Licence 2</option>
                                                    <option value="Licence 3">Licence 3</option>
                                                    <option value="Master">Master</option>
                                                </optgroup>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <input type="email" placeholder="Email d'un parent (optionnel)" value={parentEmail} onChange={e => setParentEmail(e.target.value)} className={INPUT_CYAN} />
                                        <p className="text-[10px] text-gray-400 mt-1">Un lien d'invitation lui sera envoyé pour créer son espace parent.</p>
                                    </div>
                                    <button type="submit" disabled={loading}
                                        className="w-full py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-medium transition-colors">
                                        {loading ? 'Création…' : 'Créer mon compte'}
                                    </button>
                                    <Back onClick={() => { reset(); setMode('register_type'); }} />
                                </>
                            )}
                        </form>
                    )}

                    {/* ── Inscription parent ───────────────────────────────── */}
                    {mode === 'register_parent' && (
                        <form onSubmit={handleRegisterParent} className="w-full flex flex-col gap-3">
                            <div className="mb-1">
                                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Espace parent</h2>
                                <p className="text-xs text-gray-400 mt-0.5">Créez votre compte pour suivre la scolarité de votre enfant.</p>
                            </div>
                            <ErrorMsg msg={error} />
                            <div className="flex gap-2">
                                <input type="text" placeholder="Prénom *" value={firstName} onChange={e => setFirstName(e.target.value)} required className={INPUT_ORANGE + " flex-1"} />
                                <input type="text" placeholder="Nom" value={lastName} onChange={e => setLastName(e.target.value)} className={INPUT_ORANGE + " flex-1"} />
                            </div>
                            <input type="email" placeholder="Votre email *" value={email} onChange={e => setEmail(e.target.value)} required className={INPUT_ORANGE} />
                            <input type="password" placeholder="Mot de passe (8 caractères min.)" value={password} onChange={e => setPassword(e.target.value)} required className={INPUT_ORANGE} />
                            <input type="password" placeholder="Confirmer le mot de passe" value={confirm} onChange={e => setConfirm(e.target.value)} required className={INPUT_ORANGE} />
                            <div>
                                <input type="text" placeholder="Code élève (ex. A3X7K2PQ)" value={linkCode}
                                    onChange={e => setLinkCode(e.target.value.toUpperCase())} maxLength={8}
                                    className={INPUT_ORANGE + " font-mono tracking-widest uppercase"} />
                                <p className="text-[10px] text-gray-400 mt-1">
                                    {linkCode
                                        ? `Code pré-rempli depuis l'invitation de votre enfant.`
                                        : `Visible dans les paramètres de votre enfant. Vous pouvez aussi le lier plus tard.`}
                                </p>
                            </div>
                            <button type="submit" disabled={loading}
                                className="w-full py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-medium transition-colors">
                                {loading ? 'Création…' : 'Créer mon espace parent'}
                            </button>
                            <Back onClick={() => { reset(); setMode(linkCode ? 'choice' : 'register_type'); }} />
                        </form>
                    )}

                </div>
            </div>
        </div>
    );
}

export default Login;
